import React, { useEffect, useState, ReactNode } from 'react';

// Material ui
import { Grid } from '@material-ui/core';

// Components
import Button from '@components/UI-KIT/Button';
import Footer from '@components/UI-KIT/Footer';
import DateInput from '@components/UI-KIT/Inputs/DateInput';
import FileInput from '@components/UI-KIT/Inputs/FileInput';
import TextInput from '@components/UI-KIT/Inputs/TextInput';
import AmountFormat from '@components/utils/AmountFormat';

// Types
import { TransactionDto, TransactionBetweenAccountsDto } from '@services/account-transaction';
import { postUploadFile } from '@services/files';

// Icons
import {
    LeftArrow,
    Income,
    Outgoing,
    MovementOutlined
} from '@/assets/icons';

// Styles
import VARIABLES from '@/assets/variables';

interface Props {
    transaction: TransactionDto;
    setTransaction: React.Dispatch<React.SetStateAction<TransactionDto>>;
    transactionType: string;
    transactionBetween: TransactionBetweenAccountsDto;
    setTransactionBetween: React.Dispatch<React.SetStateAction<TransactionBetweenAccountsDto>>;
    setDrawer: React.Dispatch<React.SetStateAction<{
        drawerOpened: boolean;
        drawerTitle: ReactNode | string;
        drawerChildren: ReactNode;
        drawerFooter: boolean;
        titleIcon: ReactNode;
    }>>;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
};

const ThirdStep: React.FC<Props> = ({
    transaction,
    setTransactionBetween,
    transactionBetween,
    setTransaction,
    transactionType,
    setActiveStep,
    setDrawer
}) => {
    const [ file, setFile ] = useState<File>(null);
    const [ uploading, setUploading ] = useState(false);

    const handlePostUploadFile = async (file: File) => {
        if (file === null) {
            if (transactionType !== 'Movement') setTransaction(prevState => ({
                ...prevState,
                attachProofIds: null
            }));
            else setTransactionBetween(prevState => ({
                ...prevState,
                attachProofIds: null
            }));
            setFile(file);
            return;
        }

        setUploading(true);
        const { status, data } = await postUploadFile(file, 'AccountTransaction');
        if (status === 201 && transactionType !== 'Movement') {
            setTransaction(prevState => ({
                ...prevState,
                attachProofIds: [ data[0].id ]
            }));
        }
        else if (status === 201) {
            setTransactionBetween(prevState => ({
                ...prevState,
                attachProofIds: [ data[0].id ]
            }));
        }
        setUploading(false);
        setFile(file);
    };


    useEffect(() => {
        setDrawer(prevState => ({
            ...prevState,
            drawerTitle: (
                <>
                    {`${transactionType === 'Income'
                        ? 'Ingreso de'
                        : transactionType === 'Outgoing'
                            ? 'Egreso de'
                            : 'Movimiento por'}
                     ARS`}
                    {'\xa0'}
                    <AmountFormat
                        value={transaction.amount}
                    />
                </>
            ),
            titleIcon: transactionType === 'Income'
                ? <Income />
                : transactionType === 'Outgoing'
                    ? <Outgoing />
                    : <MovementOutlined />
        }));
    }, [ ]);

    return (
        <>
            <DateInput
                label="Fecha de la operación"
                value={transactionType !== 'Movement' ? transaction.date : transactionBetween.date}
                onChange={transactionType !== 'Movement' ? (date: string) => setTransaction(prevState => ({
                    ...prevState,
                    date
                })) :
                    (date: string) => setTransactionBetween(prevState => ({
                        ...prevState,
                        date
                    }))
                }
            />
            <FileInput
                optional
                label="Adjuntar comprobante"
                placeholder={uploading ? 'Subiendo...' : 'Selecciona un archivo...'}
                value={file}
                onChange={(file: File) => handlePostUploadFile(file)}
            />
            <TextInput
                optional
                label="Descripción"
                placeholder={`¿De qué se trata este ${transactionType === 'Income' ? 'ingreso' : transactionType === 'Movement' ? 'movimiento' : 'egreso'} de plata?`}
                value={transactionType !== 'Movement' ? transaction.description : transactionBetween.description}
                onChange={transactionType !== 'Movement' ? (description: string) => setTransaction(prevState => ({
                    ...prevState,
                    description
                })) :
                    (description: string) => setTransactionBetween(prevState => ({
                        ...prevState,
                        description
                    }))
                }
            />
            <Footer
                className="AOL-Account-stepper-footer"
            >
                <Grid
                    container
                    justify="space-between"
                >
                    <Button
                        label="Anterior"
                        disabled={uploading}
                        level={3}
                        icon={(
                            <LeftArrow
                                color={VARIABLES.palette.secondary_color}
                            />
                        )}
                        onClick={() => setActiveStep(prevState => prevState - 1)}
                    />
                    <Button
                        label="Siguiente"
                        disabled={(transactionType !== 'Movement' ? !transaction.date : !transactionBetween.date) || uploading}
                        onClick={() => setActiveStep(prevState => prevState + 1)}
                    />
                </Grid>
            </Footer>
        </>
    );
};

export default ThirdStep;
